import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ca as caLocale } from "date-fns/locale/ca";
import { es as esLocale } from "date-fns/locale/es";
import { enGB as enLocale } from "date-fns/locale/en-GB";
import { Calendar } from "../components/ui/calendar";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";

const LOCALES = { ca: caLocale, es: esLocale, en: enLocale };
const INTL_LOCALES = { ca: "ca-ES", es: "es-ES", en: "en-GB" };

export default function CalendarPage() {
  const { t, lang } = useLang();
  const [experiences, setExperiences] = useState([]);
  const [month, setMonth] = useState(new Date("2026-03-01"));

  useEffect(() => {
    api.get("/experiences").then((res) => setExperiences(res.data));
  }, []);

  const eventDates = useMemo(
    () => experiences.map((e) => new Date(`${e.date}T12:00:00`)),
    [experiences],
  );

  return (
    <div className="px-6 md:px-12 pt-7 md:pt-12 pb-10 mx-auto max-w-5xl" data-testid="calendar-page">
      <div className="flex items-center justify-between md:hidden">
        <Logo />
        <LanguageSwitcher />
      </div>

      <header className="mt-9 md:mt-4">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {t("calendar")}
        </p>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-brand-brown mt-3">
          Temporada I
        </h1>
      </header>

      <div className="md:grid md:grid-cols-2 md:gap-12 mt-8 md:mt-12">
        <div className="rounded-2xl border border-brand-tan/30 bg-white/60 p-3 md:p-5">
        <Calendar
          mode="single"
          month={month}
          onMonthChange={setMonth}
          locale={LOCALES[lang] || esLocale}
          modifiers={{ event: eventDates }}
          modifiersClassNames={{
            event:
              "relative !text-brand-brown !font-bold after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-brand-gold",
          }}
          className="rounded-xl"
        />
        </div>

        <div className="mt-8 md:mt-0">
        <p className="text-[10px] tracking-[0.4em] uppercase font-bold text-brand-tan mb-3">
          {t("upcoming")}
        </p>
        <ul className="space-y-3">
          {experiences.map((e) => (
            <li key={e.id}>
              <Link
                to={`/experiences/${e.id}`}
                data-testid={`cal-row-${e.id}`}
                className="flex items-center gap-4 rounded-xl border border-brand-tan/20 bg-white/60 p-3 hover:bg-white transition-colors"
              >
                <div className="w-14 text-center">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-brand-tan">
                    {new Date(e.date).toLocaleDateString(INTL_LOCALES[lang], {
                      month: "short",
                    })}
                  </p>
                  <p className="font-serif text-3xl text-brand-brown leading-none">
                    {new Date(e.date).getDate()}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-brand-brown truncate">
                    {pick(e.title, lang)}
                  </p>
                  <p className="text-xs text-brand-tan tracking-wider uppercase truncate">
                    {e.location}
                  </p>
                </div>
                <span className="text-sm font-bold text-brand-brown">
                  €{e.price_eur}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      </div>
    </div>
  );
}
